import React, {lazy, useState, useEffect, Suspense} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useContextHook} from "use-context-hook";
import pages from "nav.json";
import Loaders from "components/atoms/Loaders";
import {AuthContext, useAuthContext} from "Context/authContext";

import Toast from "components/molecules/Toast";
import Layout from "components/Layout/index";

const importView = file =>
    lazy(() =>
        import(`./${file}.jsx`).catch(err => {
            console.error("View import error:", err);
            
            // On chunk load error (version mismatch), auto-reload to get new version
            const isChunkLoadFailed = err.name === 'ChunkLoadError' || 
                (err.message && /Loading chunk .* failed|Failed to fetch dynamically imported module/i.test(err.message));
            
            if (isChunkLoadFailed || String(err).includes("Failed to fetch")) {
                 const reloadCount = sessionStorage.getItem(`reload_count_${file}`) || 0;
                 if (reloadCount < 2) {
                     sessionStorage.setItem(`reload_count_${file}`, Number(reloadCount) + 1);
                     window.location.reload();
                     return {default: () => null};
                 }
            }

            Toast({
                type: "error",
                message: `Error in importing ${file}`,
            });
            return {default: () => <div className="p-4 text-red-500">Failed to load view: {file}</div>};
        })
    );

export default function Index() {
    const {allowedPages, loading_user} = useAuthContext();
    const history = useNavigate();
    const {view} = useParams();
    const metaViewData = pages;
    const [selectedView, setSelectedView] = useState([]);

    async function loadView(filtered) {
        const promise = filtered.map(async _view => {
            if (!_view) return null;
            const View = await importView(_view.file);

            // eslint-disable-next-line no-use-before-define
            return <View key={_view.id} selectView={selectView} />;
        });
        Promise.all(promise).then(views => setSelectedView(views.filter(v => v !== null)));
    }

    async function selectView(file) {
        const filtered = metaViewData.filter(elem => elem.file === file);

        if (filtered.length > 0) {
            loadView([filtered[0]]);
        } else {
            console.warn(`View "${file}" not found in nav.json`);
        }
    }
    useEffect(() => {
        let fileToLoad = view;
        const isAllowed =
            allowedPages.includes(fileToLoad) ||
            (fileToLoad === "manage-users" && allowedPages.includes("manage-user"));

        if (!isAllowed) {
            fileToLoad = allowedPages.length > 0 ? allowedPages[0] : "dashboard";
        }
        if (fileToLoad === "null" || fileToLoad === "/null") {
            fileToLoad = "dashboard";
        }

        if (allowedPages.length === 1 && allowedPages[0] === "no-permissions") {
            fileToLoad = "no-permissions";
        }

        if (allowedPages.includes(fileToLoad)) {
            fileToLoad = allowedPages[allowedPages.indexOf(fileToLoad)];
        }

        history(`/${fileToLoad}`, {replace: true});

        selectView(fileToLoad);
    }, [view, allowedPages, loading_user]);

    return loading_user ? (
        <Loaders viewLoader />
    ) : (
        <Layout
            merged={view === "dashboard"}
            title={view}
            showTemplate={metaViewData?.filter(elem => elem.file === view)[0]?.navigations}
            topBar>
            <Suspense fallback={<Loaders viewLoader />}>{selectedView}</Suspense>
        </Layout>
    );
}
